import { makeAutoObservable, runInAction } from 'mobx';
import {
  getEmailValidationRulesListRequest,
  createEmailValidationRuleRequest,
  deleteEmailValidationRuleRequest
} from 'services/requestAgent';
import { DIRECTION } from '../components/constants';
import { toast } from 'react-toastify';
import i18n from '../i18n';

class EmailValidationRulesStore {
  isInitialized = false;
  isLoading = false;
  isEmailValidationRuleSuccessfullyDeleted = false;
  rulesList = [];
  error = null;

  totalElements = null;
  sortColumn = {
    sort_column: 'createdAt',
    sort_direction: DIRECTION.DESC
  };
  pagination = {
    size: 20,
    page: 0,
    totalPages: null
  };

  filters = {
    search_text: '',
    action: []
  };

  constructor() {
    makeAutoObservable(this);
  }

  resetEmailValidationRulesStore = () => {
    this.isInitialized = false;
    this.isLoading = false;
    this.isEmailValidationRuleSuccessfullyDeleted = false;
    this.rulesList = [];
    this.error = null;
    this.totalElements = null;
    this.sortColumn = {
      sort_column: 'createdAt',
      sort_direction: DIRECTION.DESC
    };
    this.pagination = {
      size: 20,
      page: 0,
      totalPages: null
    };

    this.filters = {
      search_text: '',
      action: []
    };
  };

  setIsLoading = (status) => {
    this.isLoading = status;
    this.error = null;
  };

  setIsEmailValidationRuleSuccessfullyDeleted = (status) => {
    this.isEmailValidationRuleSuccessfullyDeleted = status;
  };

  setEmailValidationRulesPage = (page) => {
    this.pagination.page = page;
  };

  setEmailValidationRulesPageSize = (size) => {
    this.pagination.size = size;
  };

  setFilter = (fieldName, value) => {
    this.filters[fieldName] = value;
    this.pagination.page = 0;
  };

  setSortData = (sortData) => {
    this.sortColumn.sort_column = sortData.sortBy;
    this.sortColumn.sort_direction = sortData.direction;
  };

  setFiltersFromUrl = (params) => {
    const filters = { ...this.filters };

    // Mapping URL filter parameters to corresponding properties in the 'rulesListFilters' object
    const filterParamsMapping = {
      action: 'action',
      search_text: 'search_text'
    };

    // Iterating over each URL parameter and assigning its values to 'rulesListFilters'
    for (const param in params) {
      if (param in filterParamsMapping) {
        let value = params[param];

        // Handle only array values as arrays, keep others as their original types
        if (Array.isArray(value)) {
          filters[filterParamsMapping[param]] = value;
        } else {
          filters[filterParamsMapping[param]] = value;
        }
      }
    }

    // Converting pagination parameters from the URL
    this.pagination.page = parseInt(params.page) || this.pagination.page;
    this.pagination.size = parseInt(params.size) || this.pagination.size;

    // Converting sorting parameters from the URL
    this.sortColumn.sort_column = params.sort_column || this.sortColumn.sort_column;
    this.sortColumn.sort_direction = params.sort_direction || this.sortColumn.sort_direction;

    this.filters = filters;
  };

  prepareFiltersParams = () => {
    const params = { ...this.filters };

    if (this.filters.action) {
      params.action = this.filters.action;
    } else {
      delete params.action;
    }

    return params;
  };

  getRulesList = async () => {
    this.setIsLoading(true);

    try {
      const filtersParams = this.prepareFiltersParams();

      const res = await getEmailValidationRulesListRequest(
        this.pagination,
        this.sortColumn.sort_column,
        this.sortColumn.sort_direction,
        filtersParams.search_text,
        filtersParams
      );

      runInAction(() => {
        this.isInitialized = true;
        this.rulesList = res.content || [];
        this.totalElements = res.total_elements;
        this.pagination = {
          size: res.size,
          page: res.number,
          totalPages: res.total_pages
        };
      });
    } catch (err) {
      runInAction(() => {
        this.error = err;
      });
    } finally {
      runInAction(() => {
        this.isLoading = false;
      });
    }
  };

  createEmailValidationRule = async (data) => {
    this.setIsLoading(true);

    const toastPromise = toast.promise(createEmailValidationRuleRequest(data), {
      pending: i18n.getMessage('emailValidationRulesStore.createEmailValidationRule.toastPromise.pending'),
      success: i18n.getMessage('emailValidationRulesStore.createEmailValidationRule.toastPromise.success')
    });

    try {
      const newRule = await toastPromise;

      const updatedRulesList = [newRule, ...this.rulesList];

      runInAction(() => {
        this.rulesList = updatedRulesList;
      });
    } catch (err) {
      runInAction(() => {
        this.error = err;
      });
    } finally {
      runInAction(() => {
        this.isLoading = false;
      });
    }
  };

  deleteEmailValidationRule = async (id) => {
    this.setIsLoading(true);

    const toastPromise = toast.promise(deleteEmailValidationRuleRequest(id), {
      pending: i18n.getMessage('emailValidationRulesStore.deleteEmailValidationRule.toastPromise.pending'),
      success: i18n.getMessage('emailValidationRulesStore.deleteEmailValidationRule.toastPromise.success')
    });

    try {
      await toastPromise;

      runInAction(() => {
        this.rulesList = this.rulesList.filter((rule) => rule.id !== id);
      });
    } catch (err) {
      runInAction(() => {
        this.error = err;
      });
    } finally {
      runInAction(() => {
        this.isLoading = false;
      });
    }
  };
}

export default new EmailValidationRulesStore();
